import type { ThirdPartyResponseByIdResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetThirdPartyResponseSubscriptionByIdSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetThirdPartyResponseSubscriptionByIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createSubscriptionHook } from 'src/utils';

/**
 * `Status` drives which review actions are enabled on this page, and it can move
 * under the reviewer's feet — a respondent submitting, or another reviewer
 * approving. The V3 stack has no subscription transport, so the row is POLLED at
 * 5s, matching the other focused feeds.
 */
const POLL_INTERVAL_MS = 5000;

/**
 * Holding-pattern subscription hook for a single third-party response
 * (COI-581) — backs the response review page.
 *
 * Reuses the existing `frontend.thirdPartyResponse.getById` procedure: its query
 * config already covers this subscription's full selection (`Schema`/`UISchema`,
 * `files`, `thirdParty.ancestorContributors`, `questionnaireTemplateVersion`).
 *
 * - `trpc` ON  → polls `frontend.thirdPartyResponse.getById`.
 * - `trpc` OFF → the legacy Hasura `getThirdPartyResponseSubscriptionById`
 *   subscription.
 */
export const useGetThirdPartyResponseByIdSubscription = createSubscriptionHook<
  { id: string },
  ThirdPartyResponseByIdResponseRow | null,
  GetThirdPartyResponseSubscriptionByIdSubscription
>({
  trpcQueryOptions: (trpc, { id }) =>
    trpc.frontend.thirdPartyResponse.getById.queryOptions({ id }),
  trpcRefetchInterval: () => POLL_INTERVAL_MS,
  mapTrpcDataToGraphQL: (row) => ({
    third_party_response_by_pk: row ?? null,
  }),
  graphqlDocument: GetThirdPartyResponseSubscriptionByIdDocument,
  graphqlVariables: ({ id }) => ({ Id: id }),
});
